'use client'

import * as React from 'react'
import { Loader2, X } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Card } from '@/components/ui/card'
import { StatusBadge, type StatusVariant } from '@/components/ui/status-badge'
import { UserText } from '@/components/ui/user-text'
import { formatLearnerNumber } from '@/components/user-portal/learner-i18n'
import { richText } from '@/lib/i18n/rich-text'
import { formatActivityExact, formatActivityRelative } from '@/lib/time/activity-time'
import { useTranslation } from '@/lib/i18n'
import { cn } from '@/lib/utils'
import { handled, isOpenState, isProgressing, type ActivityDto, type ActivityState } from '@/lib/activities/types'
import { rendererFor } from './activity-dock-renderers'

const STATE_VARIANT: Record<ActivityState, StatusVariant> = {
  queued: 'info',
  running: 'info',
  paused: 'warning',
  finished: 'success',
  finished_with_failures: 'warning',
  cancelled: 'muted',
  failed: 'destructive',
}

/** The stacked bar: done (green), failed (red), skipped (muted), over a track of what is left. */
export function ActivityBar({ counts, label }: { counts: ActivityDto['counts']; label: string }) {
  const pct = (n: number) => (counts.total > 0 ? Math.min(100, (n / counts.total) * 100) : 0)
  return (
    <div
      role="progressbar"
      aria-label={label}
      aria-valuemin={0}
      aria-valuemax={counts.total}
      aria-valuenow={handled(counts)}
      className="flex h-1.5 w-full overflow-hidden rounded-full bg-primary/20"
    >
      <div className="h-full bg-emerald-500 transition-all motion-reduce:transition-none" style={{ width: `${pct(counts.done)}%` }} />
      <div className="h-full bg-destructive transition-all motion-reduce:transition-none" style={{ width: `${pct(counts.failed)}%` }} />
      <div className="h-full bg-foreground/20 transition-all motion-reduce:transition-none" style={{ width: `${pct(counts.skipped)}%` }} />
    </div>
  )
}

interface ActivityCardProps {
  activity: ActivityDto
  expanded: boolean
  onToggle: () => void
  onDismiss: (a: ActivityDto) => void
}

/**
 * One activity in the drawer: the module, the title, the state, the bar and how far along it is;
 * opened, the figures (the server's, never recomputed), the failures by reason, who started it and
 * when, and the module's own buttons. The chrome is shared; the words and the buttons come from the
 * module's renderer.
 */
export function ActivityCard({ activity, expanded, onToggle, onDismiss }: ActivityCardProps) {
  const { t, locale } = useTranslation()
  const n = (v: number) => formatLearnerNumber(v, locale)
  const r = rendererFor(activity.kind)
  const c = activity.counts
  const open = isOpenState(activity.state)
  const bodyId = `activity-body-${activity.id}`
  const titleText = r.titleText(activity, t, n)

  const rightText = open
    ? t('activities.card.left', { count: c.remaining })
    : activity.state === 'cancelled'
      ? t('activities.card.notDone', { count: Math.max(0, c.total - c.done) })
      : c.failed > 0
        ? t('activities.card.failedCount', { count: c.failed })
        : ''

  const failedTotal = activity.failedByReason.reduce((sum, x) => sum + x.count, 0)
  const startedWhen = activity.startedAt ? formatActivityRelative(activity.startedAt, t, locale) : ''
  const startedExact = activity.startedAt ? formatActivityExact(activity.startedAt, locale) : ''
  const by = activity.startedBy?.name

  return (
    <Card
      className="gap-0 py-0 shadow-sm"
      data-activity-id={activity.id}
      data-activity-state={activity.state}
    >
      {/* The button holds only text: a button's children are presentational, so the bar (a progressbar) sits beside it. */}
      <div className="px-4 pt-4">
        <button
          type="button"
          aria-expanded={expanded}
          aria-controls={bodyId}
          onClick={onToggle}
          className="-mx-2 block w-[calc(100%+1rem)] rounded-lg px-2 py-1 text-start hover:bg-accent/50 focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
        >
          <span className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-xs text-muted-foreground">{r.moduleLabel(t)}</span>
            <StatusBadge
              variant={STATE_VARIANT[activity.state]}
              icon={isProgressing(activity.state) ? <Loader2 className="size-3 animate-spin motion-reduce:animate-none" aria-hidden /> : undefined}
            >
              {t(`activities.state.${activity.state}`)}
            </StatusBadge>
          </span>
          <span className="mt-0.5 block text-sm font-semibold">{r.title(activity, t, n)}</span>
        </button>
        <div className="mt-2.5">
          <ActivityBar counts={c} label={titleText} />
        </div>
        <div className="mt-1.5 flex items-center justify-between gap-2 pb-4 text-xs tabular-nums text-muted-foreground">
          <span>{t(`activities.card.progress.${activity.unit}`, { done: n(handled(c)), total: n(c.total) })}</span>
          <span>{rightText}</span>
        </div>
      </div>

      <div id={bodyId} hidden={!expanded} className={cn('space-y-3 px-4 pb-4', !expanded && 'hidden')}>
        {expanded && (
          <>
            <div className="grid grid-cols-2 gap-2">
              {r.tiles(activity, t, n).map((tile) => (
                <div key={tile.key} className="rounded-lg border px-3 py-2" data-activity-tile={tile.key}>
                  <div className={cn('text-lg font-bold tabular-nums', tile.tone === 'bad' && 'text-destructive')}>{tile.value}</div>
                  <div className="text-xs text-muted-foreground">{tile.label}</div>
                </div>
              ))}
            </div>

            <div data-activity-failures>
              <div className="flex items-center justify-between text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                <span>{t('activities.card.failedByReason')}</span>
                <span className="tabular-nums">{n(c.failed)}</span>
              </div>
              {c.failed === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">{t('activities.card.noFailures')}</p>
              ) : failedTotal === 0 ? (
                <p className="mt-1 text-sm text-muted-foreground">{t('activities.card.noReasons')}</p>
              ) : (
                <ul className="mt-1 divide-y text-sm">
                  {activity.failedByReason.map((x) => (
                    <li key={`${x.code}-${x.retryable}`} className="flex items-start justify-between gap-3 py-1.5">
                      <span>
                        {r.reasonLabel(t, x.code)}
                        {x.retryable === true && <span className="text-muted-foreground"> · {t('activities.card.canRetry')}</span>}
                        {x.retryable === false && <span className="text-muted-foreground"> · {t('activities.card.cannotRetry')}</span>}
                      </span>
                      <span className="font-medium tabular-nums">{n(x.count)}</span>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            {(startedWhen || by) && (
              <p className="text-xs text-muted-foreground" title={startedExact || undefined}>
                {by
                  ? richText(t('activities.card.startedBy'), { name: <UserText>{by}</UserText>, when: startedWhen })
                  : t('activities.card.startedAt', { when: startedWhen })}
              </p>
            )}

            <div className="flex flex-wrap items-center gap-2">
              <r.Actions activity={activity} />
              {!open && (
                <Button size="sm" variant="ghost" onClick={() => onDismiss(activity)} data-activity-action="dismiss">
                  <X className="size-4" aria-hidden />
                  {t('activities.card.dismiss')}
                </Button>
              )}
            </div>
          </>
        )}
      </div>
    </Card>
  )
}
